'use client'

import { AlumniaLogo } from '@/components/alumnia-logo'

import Link from 'next/link'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetClose } from '@/components/ui/sheet'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { Menu, X, User, Settings, LogOut } from 'lucide-react'
import { useTranslations, useLocale } from 'next-intl'
import { localizedPath } from '@/lib/i18n/localized-path'

interface UserInfo {
  firstName?: string
  avatarUrl?: string
  email?: string
}

interface NavbarProps {
  isLoggedIn?: boolean
  dashboardUrl?: string | null
  user?: UserInfo | null
}

export function Navbar({ isLoggedIn, dashboardUrl, user }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const t = useTranslations('landing')
  const locale = useLocale()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const handleSignOut = async () => {
    const { performSignOut } = await import('@/lib/utils/sign-out')
    await performSignOut()
  }

  const avatarInitial =
    user?.firstName?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || 'U'

  return (
    <nav
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        scrolled
          ? 'bg-white/80 backdrop-blur-md border-b border-border/50 py-3'
          : 'bg-transparent py-5'
      )}
    >
      <div className="container mx-auto px-4 flex items-center justify-between">
        {/* Logo */}
        <Link href={localizedPath(locale, '/')} className="flex items-center gap-2 z-50">
          <AlumniaLogo size={32} variant="brand" />
          <span className="font-serif text-xl font-bold tracking-tight text-foreground">
            Alumnia
          </span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-3">
          <Link href="/#demo">
            <Button variant="ghost" className="font-medium">
              {t('nav.tour')}
            </Button>
          </Link>
          <Link href="/#how-it-works">
            <Button variant="ghost" className="font-medium">
              {t('nav.howItWorks')}
            </Button>
          </Link>
          <Link href="/#security">
            <Button variant="ghost" className="font-medium">
              {t('nav.security')}
            </Button>
          </Link>
          <Link href={localizedPath(locale, '/pricing')}>
            <Button variant="ghost" className="font-medium">
              {t('nav.pricing')}
            </Button>
          </Link>

          {isLoggedIn ? (
            // Logged in: Dashboard button + Avatar dropdown (like Supabase)
            <>
              {dashboardUrl ? (
                <Link href={dashboardUrl}>
                  <Button className="font-medium">{t('nav.dashboard')}</Button>
                </Link>
              ) : (
                <Link href="/settings">
                  <Button className="font-medium">{t('nav.myAccount')}</Button>
                </Link>
              )}

              {/* Avatar Dropdown for account actions */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-full h-9 w-9 hover:bg-slate-100"
                  >
                    <Avatar className="w-8 h-8 border border-border">
                      <AvatarImage src={user?.avatarUrl} />
                      <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                        {avatarInitial}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link href="/profile" className="flex items-center">
                      <User className="mr-2 h-4 w-4" />
                      {t('nav.profile')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild className="cursor-pointer">
                    <Link href="/settings" className="flex items-center">
                      <Settings className="mr-2 h-4 w-4" />
                      {t('nav.settings')}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={handleSignOut}
                    className="cursor-pointer text-red-600 focus:text-red-600 focus:bg-red-50"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t('nav.logOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            // Not logged in: Sign In + Get Started
            <>
              <Link href="/login">
                <Button variant="ghost" className="font-medium">
                  {t('nav.signIn')}
                </Button>
              </Link>
              <Link href="/start">
                <Button className="font-medium">{t('nav.getStarted')}</Button>
              </Link>
            </>
          )}
        </div>

        {/* Mobile Navigation */}
        <div className="md:hidden flex items-center gap-2">
          {/* Show avatar before hamburger when logged in */}
          {isLoggedIn && user && (
            <Avatar className="w-7 h-7 border border-border">
              <AvatarImage src={user.avatarUrl} />
              <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-medium">
                {avatarInitial}
              </AvatarFallback>
            </Avatar>
          )}

          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setIsOpen(true)}>
              <Menu className="h-6 w-6" />
              <span className="sr-only">Toggle menu</span>
            </Button>

            {/* Full-screen brand blue mobile menu */}
            <SheetContent
              side="right"
              className="w-full sm:w-full border-none bg-[#1e3a8a] p-0 flex flex-col [&>button]:hidden"
            >
              {/* Custom header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-white/10">
                <div className="flex items-center gap-2">
                  <AlumniaLogo size={32} variant="light" />
                  <span className="font-serif text-xl font-bold text-white">Alumnia</span>
                </div>
                <SheetClose asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white/70 hover:text-white hover:bg-white/10 h-9 w-9"
                  >
                    <X className="w-6 h-6" />
                    <span className="sr-only">Close menu</span>
                  </Button>
                </SheetClose>
              </div>

              {/* Navigation items */}
              <nav className="flex-1 flex flex-col px-4 py-6">
                <Link href="/#demo" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center h-14 px-4 rounded-lg text-white/90 hover:bg-white/10 text-lg font-medium transition-colors">
                    {t('nav.tour')}
                  </div>
                </Link>
                <Link href="/#how-it-works" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center h-14 px-4 rounded-lg text-white/90 hover:bg-white/10 text-lg font-medium transition-colors">
                    {t('nav.howItWorks')}
                  </div>
                </Link>
                <Link href="/#security" onClick={() => setIsOpen(false)}>
                  <div className="flex items-center h-14 px-4 rounded-lg text-white/90 hover:bg-white/10 text-lg font-medium transition-colors">
                    {t('nav.security')}
                  </div>
                </Link>
                <Link href={localizedPath(locale, '/pricing')} onClick={() => setIsOpen(false)}>
                  <div className="flex items-center h-14 px-4 rounded-lg text-white/90 hover:bg-white/10 text-lg font-medium transition-colors">
                    {t('nav.pricing')}
                  </div>
                </Link>

                {!isLoggedIn && (
                  <Link href="/login" onClick={() => setIsOpen(false)}>
                    <div className="flex items-center h-14 px-4 rounded-lg text-white/90 hover:bg-white/10 text-lg font-medium transition-colors">
                      {t('nav.signIn')}
                    </div>
                  </Link>
                )}

                {isLoggedIn && (
                  <>
                    <Link href="/profile" onClick={() => setIsOpen(false)}>
                      <div className="flex items-center gap-3 h-14 px-4 rounded-lg text-white/90 hover:bg-white/10 text-lg font-medium transition-colors">
                        <User className="w-5 h-5" />
                        {t('nav.profile')}
                      </div>
                    </Link>
                    <Link href="/settings" onClick={() => setIsOpen(false)}>
                      <div className="flex items-center gap-3 h-14 px-4 rounded-lg text-white/90 hover:bg-white/10 text-lg font-medium transition-colors">
                        <Settings className="w-5 h-5" />
                        {t('nav.settings')}
                      </div>
                    </Link>
                    <button
                      onClick={() => {
                        setIsOpen(false)
                        handleSignOut()
                      }}
                      className="flex items-center gap-3 h-14 px-4 rounded-lg text-red-300 hover:bg-white/10 text-lg font-medium transition-colors w-full text-left"
                    >
                      <LogOut className="w-5 h-5" />
                      {t('nav.logOut')}
                    </button>
                  </>
                )}
              </nav>

              {/* Bottom-anchored primary CTA */}
              <div className="p-6 border-t border-white/10">
                {isLoggedIn && dashboardUrl ? (
                  <Link href={dashboardUrl} onClick={() => setIsOpen(false)}>
                    <Button className="w-full h-14 text-lg bg-white text-primary hover:bg-white/90 font-semibold">
                      {t('nav.goToDashboard')}
                    </Button>
                  </Link>
                ) : isLoggedIn ? (
                  <Link href="/settings" onClick={() => setIsOpen(false)}>
                    <Button className="w-full h-14 text-lg bg-white text-primary hover:bg-white/90 font-semibold">
                      {t('nav.myAccount')}
                    </Button>
                  </Link>
                ) : (
                  <Link href="/start" onClick={() => setIsOpen(false)}>
                    <Button className="w-full h-14 text-lg bg-white text-primary hover:bg-white/90 font-semibold">
                      {t('nav.getStarted')}
                    </Button>
                  </Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </nav>
  )
}
